import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { fireCheckoutStepEvent } from '@/lib/tracking';
import type { CartResponse } from '@/types/cart';

/**
 * Sets up a Zustand subscriber that fires pixel events on step transitions.
 *
 * IMPORTANT: Uses useCheckoutStore.subscribe (static method) inside useEffect,
 * NOT the hook form. This prevents React 18 Strict Mode double-fires:
 * - useEffect runs once on mount, returns unsubscribe as cleanup.
 * - Strict Mode unmount/remount cycle cleans up and re-registers once.
 * - The subscriber itself runs outside React's render cycle — no double-fire risk.
 *
 * Per TRKN-06: tracking fires from store actions (subscribe), not React effects on state.
 */
export function useTrackingSubscriber(): void {
    const queryClient = useQueryClient();

    useEffect(() => {
        const unsubscribe = useCheckoutStore.subscribe(
            // Full-state subscriber — compare only the two fields that trigger events.
            // The 2-argument (selector, callback) form requires subscribeWithSelector middleware
            // which the store does not use. Use the standard 1-argument form and compare manually.
            (curr, prev) => {
                // Read cart from TanStack Query cache — not via hook (hooks cannot be called here)
                const cart = queryClient.getQueryData<CartResponse>(['cart']) ?? null;

                // Step transition: fires step-level pixel events
                if (curr.currentStep !== prev.currentStep) {
                    fireCheckoutStepEvent(curr.currentStep, cart);
                }

                // Purchase is fired exclusively from the thank-you page
                // (firePurchaseFromOrderDetails), gated on the order's paid
                // status. Firing here as well would (a) count unpaid offline
                // orders and (b) double-count with a different event_id, since
                // the Store-API success response sets orderResult even for
                // pending/on-hold orders.
            }
        );

        // Cleanup: tear down subscriber when component unmounts
        // Same pattern as clearTimeout in useAbandonedCart.ts
        return unsubscribe;
    }, []); // [] safe: queryClient and subscribe refs are stable
}
